// ✅ src/screens/CallScreen.js (FULL) — FIX remoteCount=0 + FIX duplicate listeners + FIX UID_CONFLICT
// ✅ UPDATED PER YOUR REQUEST:
// 1) ✅ REMOVED: Debug drawer + debug state + debug styles
// 2) ✅ Ringtone: tries to autoplay; if blocked, stays silent (NO "Tap to enable ringtone" UI)
// ✅ Aside that, NOTHING ELSE touched.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AgoraRTC from "agora-rtc-sdk-ng";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  increment,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import { createNotification } from "../lib/notify";

/* ---------------- helpers ---------------- */
function safeStr(v) {
  return String(v ?? "").trim();
}
function num(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}
function initials(name) {
  const t = safeStr(name);
  if (!t) return "?";
  const parts = t.split(" ").filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts[1]?.[0] ?? "";
  return (a + b).toUpperCase();
}
function msFromTs(ts) {
  try {
    if (!ts) return null;
    if (typeof ts === "number") return ts;
    if (ts?.toMillis) return ts.toMillis();
    if (ts?.toDate) return ts.toDate().getTime();
    return null;
  } catch {
    return null;
  }
}
function fmtMMSS(sec) {
  const s = Math.max(0, num(sec, 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}
function isPermissionError(e) {
  const name = safeStr(e?.name);
  const msg = safeStr(e?.message).toLowerCase();
  return (
    name === "NotAllowedError" ||
    name === "PermissionDeniedError" ||
    msg.includes("permission")
  );
}

function parseMatchId(matchId) {
  const t = safeStr(matchId);
  const parts = t.split("__").filter(Boolean);
  if (parts.length !== 2) return { a: "", b: "" };
  return { a: parts[0], b: parts[1] };
}
function normalizePair(a, b) {
  const A = safeStr(a);
  const B = safeStr(b);
  return [A, B].sort().join("__");
}
function otherUnreadField(match, myUid) {
  return safeStr(match?.userA) === safeStr(myUid) ? "unreadCountB" : "unreadCountA";
}

const RING_TIMEOUT_MS = 30000;

export default function CallScreen() {
  const nav = useNavigate();
  const loc = useLocation();
  const { matchId: matchIdParam } = useParams();

  const qs = useMemo(() => new URLSearchParams(loc.search), [loc.search]);
  const mode = safeStr(loc.state?.mode) || safeStr(qs.get("mode")) || "voice"; // voice|video
  const urlCallId = safeStr(loc.state?.callId) || safeStr(qs.get("callId"));
  const isVideoRequested = safeStr(mode) === "video";
  const matchId = safeStr(matchIdParam);

  // ✅ stable channel even if matchId order flips
  const CHANNEL = useMemo(() => {
    const { a, b } = parseMatchId(matchId);
    if (!a || !b) return matchId;
    return normalizePair(a, b);
  }, [matchId]);

  // Agora
  const APP_ID = safeStr(process.env.REACT_APP_AGORA_APP_ID);
  const TOKEN = null;

  // refs
  const clientRef = useRef(null);
  const bigVideoRef = useRef(null);
  const pipVideoRef = useRef(null);
  const tracksRef = useRef({ mic: null, cam: null });

  // ✅ prevent double join
  const joiningRef = useRef(false);

  // ringtone
  const ringAudioRef = useRef(null);

  // state
  const [me, setMe] = useState(null);
  const [match, setMatch] = useState(null);
  const [other, setOther] = useState({ uid: "", displayName: "User", avatar: "" });

  const [callId, setCallId] = useState(urlCallId || "");
  const [callDoc, setCallDoc] = useState(null);
  const createdByMeRef = useRef(false);

  const [uiStatus, setUiStatus] = useState("loading");
  const [timerSec, setTimerSec] = useState(0);
  const tickRef = useRef(null);
  const ringTimerRef = useRef(null);

  const [agoraJoined, setAgoraJoined] = useState(false);
  const [agoraErr, setAgoraErr] = useState("");
  const [swapViews, setSwapViews] = useState(false);

  const [remoteUsersState, setRemoteUsersState] = useState([]); // [{uid, hasVideo, hasAudio}]
  const [remoteVideoUid, setRemoteVideoUid] = useState("");

  // ✅ refs for latest values (so Agora handlers never depend on changing state)
  const swapViewsRef = useRef(false);
  const remoteVideoUidRef = useRef("");
  const isVideoRequestedRef = useRef(false);

  useEffect(() => {
    swapViewsRef.current = !!swapViews;
  }, [swapViews]);
  useEffect(() => {
    remoteVideoUidRef.current = String(remoteVideoUid || "");
  }, [remoteVideoUid]);
  useEffect(() => {
    isVideoRequestedRef.current = !!isVideoRequested;
  }, [isVideoRequested]);

  const icon = isVideoRequested ? "🎥" : "📞";
  const headTitle = safeStr(other.displayName) || "Call";
  const showVideoLayout = isVideoRequested && uiStatus === "in_call";

  // ✅ Debug removed (keep calls as no-op so we don't touch logic)
  function dbgPush() {}

  /* ---------------- ringtone helpers ---------------- */
  async function startRingtone() {
    try {
      let a = ringAudioRef.current;
      if (!a) {
        a = new Audio("/ringtone.mp3");
        a.loop = true;
        a.volume = 0.9;
        ringAudioRef.current = a;
      }
      a.currentTime = 0;

      // ✅ Try autoplay. If blocked, stay silent (NO UI).
      await a.play().catch(() => {});
      dbgPush("ringtone:attempted");
    } catch {}
  }
  function stopRingtone() {
    try {
      const a = ringAudioRef.current;
      if (!a) return;
      a.pause();
      a.currentTime = 0;
      dbgPush("ringtone:stopped");
    } catch {}
  }

  /* ---------------- auth ---------------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) {
        nav("/login", { replace: true });
        return;
      }
      setMe({ uid: u.uid, email: safeStr(u.email) });
    });
    return () => unsub();
  }, [nav]);

  /* ---------------- keep callId from URL in state ---------------- */
  useEffect(() => {
    if (urlCallId && urlCallId !== callId) setCallId(urlCallId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCallId]);

  /* ---------------- cleanup on unmount ---------------- */
  useEffect(() => {
    return () => {
      stopRingtone();
      (async () => {
        try {
          const client = clientRef.current;
          const { mic, cam } = tracksRef.current || {};
          try {
            mic?.stop?.();
            mic?.close?.();
          } catch {}
          try {
            cam?.stop?.();
            cam?.close?.();
          } catch {}
          tracksRef.current = { mic: null, cam: null };
          try {
            await client?.leave?.();
          } catch {}
        } catch {}
      })();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- load match + other profile ---------------- */
  useEffect(() => {
    if (!me?.uid || !matchId) return;

    let alive = true;

    (async () => {
      try {
        const matchRef = doc(db, "matches", matchId);
        let snap = await getDoc(matchRef);

        if (!snap.exists()) {
          const { a, b } = parseMatchId(matchId);
          if (!a || !b || (me.uid !== a && me.uid !== b)) {
            if (!alive) return;
            setUiStatus("error");
            return;
          }

          await setDoc(
            matchRef,
            {
              createdAt: serverTimestamp(),
              isActive: true,
              lastMessageAt: serverTimestamp(),
              lastMessageText: "",
              unreadCountA: 0,
              unreadCountB: 0,
              userA: a,
              userB: b,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );

          snap = await getDoc(matchRef);
        }

        const m = snap.data() || {};
        if (!alive) return;

        setMatch({ id: snap.id, ...m });

        const otherUid = safeStr(m.userA) === me.uid ? safeStr(m.userB) : safeStr(m.userA);

        try {
          const profSnap = await getDoc(doc(db, "datingProfiles", otherUid));
          if (profSnap.exists()) {
            const p = profSnap.data() || {};
            setOther({
              uid: otherUid,
              displayName: safeStr(p.displayName) || "User",
              avatar: safeStr(p.avatar) || safeStr(p.photos?.[0]) || "",
            });
          } else {
            setOther({ uid: otherUid, displayName: "User", avatar: "" });
          }
        } catch {
          setOther({ uid: otherUid, displayName: "User", avatar: "" });
        }
      } catch (e) {
        console.error("CallScreen match load error:", e);
        setUiStatus("error");
      }
    })();

    return () => {
      alive = false;
    };
  }, [me?.uid, matchId]);

  /* ---------------- remote helpers (stable) ---------------- */
  const snapshotRemoteToState = useCallback(() => {
    try {
      const client = clientRef.current;
      const list = (client?.remoteUsers || []).map((u) => ({
        uid: String(u?.uid ?? ""),
        hasVideo: !!u?.videoTrack,
        hasAudio: !!u?.audioTrack,
      }));
      setRemoteUsersState(list);
      return list;
    } catch {
      setRemoteUsersState([]);
      return [];
    }
  }, []);

  function safePlay(track, el) {
    try {
      if (!el) return;
      el.innerHTML = "";
      if (!track) return;
      track.play(el);
    } catch {}
  }

  function pickRemoteVideoUser(preferredUid = "") {
    try {
      const client = clientRef.current;
      const ru = client?.remoteUsers || [];
      const pref = String(preferredUid || remoteVideoUidRef.current || "");
      if (pref) {
        const p = ru.find((u) => String(u?.uid) === pref);
        if (p) return p;
      }
      return ru.find((u) => !!u?.videoTrack) || ru[0] || null;
    } catch {
      return null;
    }
  }

  // ✅ stable: does NOT depend on swapViews state (uses ref)
  const replayVideo = useCallback(
    (preferredUid = "") => {
      try {
        const cam = tracksRef.current?.cam;
        const remoteUser = pickRemoteVideoUser(preferredUid);
        const remoteTrack = remoteUser?.videoTrack || null;

        const bigEl = bigVideoRef.current;
        const pipEl = pipVideoRef.current;
        if (!bigEl || !pipEl) return;

        const swapped = !!swapViewsRef.current;

        if (swapped) {
          safePlay(cam, bigEl);
          safePlay(remoteTrack, pipEl);
        } else {
          safePlay(remoteTrack, bigEl);
          safePlay(cam, pipEl);
        }

        snapshotRemoteToState();
      } catch {}
    },
    [snapshotRemoteToState]
  );

  /* ---------------- Agora client (TRULY ONCE) ---------------- */
  useEffect(() => {
    const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
    clientRef.current = client;

    const onConn = (cur, prev, reason) => {
      dbgPush("conn", { prev, cur, reason });
    };

    const onUserJoined = (u) => {
      dbgPush("user-joined", { uid: u?.uid });
      snapshotRemoteToState();
      replayVideo(String(u?.uid ?? ""));
    };

    const onUserLeft = (u) => {
      dbgPush("user-left", { uid: u?.uid });
      snapshotRemoteToState();
      replayVideo();
    };

    const onUserPublished = async (user, mediaType) => {
      dbgPush("user-published", { uid: user?.uid, mediaType });
      try {
        await client.subscribe(user, mediaType);

        if (mediaType === "audio") user.audioTrack?.play?.();

        snapshotRemoteToState();

        if (mediaType === "video") {
          const uidStr = String(user?.uid ?? "");
          setRemoteVideoUid(uidStr);
          replayVideo(uidStr);
        } else {
          replayVideo();
        }
      } catch (e) {
        setAgoraErr(safeStr(e?.message) || "Subscribe failed");
        dbgPush("subscribe-failed", { msg: e?.message });
      }
    };

    const onUserUnpublished = (u, mediaType) => {
      dbgPush("user-unpublished", { uid: u?.uid, mediaType });
      snapshotRemoteToState();
      replayVideo();
    };

    const onException = (e) => dbgPush("exception", { code: e?.code, msg: e?.msg || e?.message });

    client.on("connection-state-change", onConn);
    client.on("user-joined", onUserJoined);
    client.on("user-left", onUserLeft);
    client.on("user-published", onUserPublished);
    client.on("user-unpublished", onUserUnpublished);
    client.on("exception", onException);

    return () => {
      try {
        client.off("connection-state-change", onConn);
        client.off("user-joined", onUserJoined);
        client.off("user-left", onUserLeft);
        client.off("user-published", onUserPublished);
        client.off("user-unpublished", onUserUnpublished);
        client.off("exception", onException);
      } catch {}
      try {
        clientRef.current = null;
      } catch {}
    };
    // ✅ EMPTY deps: never recreate client
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- Agora join/leave ---------------- */
  async function agoraJoinIfNeeded() {
    setAgoraErr("");

    if (!APP_ID) {
      setAgoraErr("Missing REACT_APP_AGORA_APP_ID in .env");
      dbgPush("agora-missing-appid");
      return;
    }
    if (agoraJoined) return;
    if (joiningRef.current) return;

    const client = clientRef.current;
    if (!client) return;

    joiningRef.current = true;

    try {
      // ✅ FIX UID_CONFLICT: let Agora assign a unique UID
      dbgPush("agora:join", { channel: CHANNEL, uid: "AUTO" });
      await client.join(APP_ID, CHANNEL, TOKEN, null);

      snapshotRemoteToState();

      if (isVideoRequestedRef.current) {
        try {
          const [mic, cam] = await AgoraRTC.createMicrophoneAndCameraTracks();
          tracksRef.current = { mic, cam };
          await client.publish([mic, cam]);
          dbgPush("agora:published", { mic: true, cam: true });
          replayVideo();
        } catch (e) {
          const mic = await AgoraRTC.createMicrophoneAudioTrack();
          tracksRef.current = { mic, cam: null };
          await client.publish([mic]);
          dbgPush("agora:published", { mic: true, cam: false });

          if (isPermissionError(e)) setAgoraErr("Camera permission denied. Continuing with audio only.");
          else setAgoraErr(safeStr(e?.message) || "Camera failed. Continuing with audio only.");

          replayVideo();
        }
      } else {
        const mic = await AgoraRTC.createMicrophoneAudioTrack();
        tracksRef.current = { mic, cam: null };
        await client.publish([mic]);
        dbgPush("agora:published", { mic: true, cam: false });
        replayVideo();
      }

      setAgoraJoined(true);
      snapshotRemoteToState();
      dbgPush("agora:joined");
    } catch (e) {
      setAgoraErr(safeStr(e?.message) || "Agora join failed");
      dbgPush("agora:join-fail", { msg: e?.message });
      try {
        await client?.leave?.();
      } catch {}
    } finally {
      joiningRef.current = false;
    }
  }

  async function agoraLeave() {
    try {
      const client = clientRef.current;
      const { mic, cam } = tracksRef.current || {};

      try {
        mic?.stop?.();
        mic?.close?.();
      } catch {}
      try {
        cam?.stop?.();
        cam?.close?.();
      } catch {}

      tracksRef.current = { mic: null, cam: null };

      if (bigVideoRef.current) bigVideoRef.current.innerHTML = "";
      if (pipVideoRef.current) pipVideoRef.current.innerHTML = "";

      try {
        await client?.leave?.();
      } catch {}

      setAgoraJoined(false);
      setRemoteVideoUid("");
      setRemoteUsersState([]);
      dbgPush("agora:left");
    } catch {}
  }

  /* ---------------- create call doc (CALLER ONLY) ---------------- */
  useEffect(() => {
    if (!me?.uid || !matchId || !other?.uid || !match) return;
    if (callId) return;

    let alive = true;

    (async () => {
      try {
        createdByMeRef.current = true;

        const newCallRef = doc(collection(db, "calls"));
        const newId = newCallRef.id;
        setCallId(newId);

        await setDoc(
          newCallRef,
          {
            matchId: safeStr(matchId),
            fromUid: safeStr(me.uid),
            toUid: safeStr(other.uid),
            mode: safeStr(mode) || "voice",
            status: "ringing",
            createdAt: serverTimestamp(),
            answeredAt: null,
            endedAt: null,
            chatLogged: false,
            loggedToChat: false,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        nav(`/call/${matchId}?callId=${newId}&mode=${safeStr(mode) || "voice"}`, {
          replace: true,
          state: { ...(loc.state || {}), callId: newId, mode: safeStr(mode) || "voice" },
        });

        try {
          const myLabel = safeStr(auth.currentUser?.displayName) || safeStr(me.email) || "Someone";
          await createNotification(other.uid, {
            type: "CALL",
            title: isVideoRequested ? "Incoming video call" : "Incoming voice call",
            message: `${myLabel} is calling you`,
            data: {
              route: `/call/${matchId}?callId=${newId}&mode=${safeStr(mode) || "voice"}`,
              matchId: safeStr(matchId),
              callId: newId,
              fromUid: safeStr(me.uid),
              mode: safeStr(mode) || "voice",
            },
          });
        } catch {}

        if (!alive) return;
        setUiStatus("ringing");
        dbgPush("call:created", { callId: newId });
      } catch (e) {
        console.error("create call error:", e);
        if (!alive) return;
        setUiStatus("error");
        dbgPush("call:create-failed", { msg: e?.message });
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.uid, me?.email, matchId, other?.uid, match, callId, mode, isVideoRequested, nav]);

  /* ---------------- subscribe to call doc (ONCE per callId) ---------------- */
  const callSubRef = useRef({ callId: "", unsub: null });

  useEffect(() => {
    if (!callId) return;

    // ✅ guard: if already subscribed to this callId, do nothing
    if (callSubRef.current.callId === callId && typeof callSubRef.current.unsub === "function") {
      return;
    }

    // ✅ cleanup previous subscription (if any)
    try {
      if (typeof callSubRef.current.unsub === "function") callSubRef.current.unsub();
    } catch {}

    dbgPush("call:subscribe", { callId });

    const ref = doc(db, "calls", callId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setCallDoc(null);
          setUiStatus("error");
          dbgPush("call:missing");
          return;
        }

        const d = snap.data() || {};
        const st = safeStr(d.status);

        setCallDoc({ id: snap.id, ...d });

        if (st === "ringing") {
          if (!createdByMeRef.current && safeStr(d.toUid) === safeStr(me?.uid)) setUiStatus("incoming");
          else setUiStatus("ringing");
        } else if (st === "answered") setUiStatus("in_call");
        else if (st === "ended") setUiStatus("ended");
        else if (st === "missed") setUiStatus("missed");
        else if (st === "declined") setUiStatus("declined");
        else setUiStatus("error");

        dbgPush("call:update", { status: st, fromUid: d.fromUid, toUid: d.toUid, answeredAt: !!d.answeredAt });
      },
      (err) => {
        setUiStatus("error");
        dbgPush("call:snapshot-error", { msg: err?.message });
      }
    );

    callSubRef.current = { callId, unsub };

    return () => {
      try {
        if (callSubRef.current.callId === callId && typeof callSubRef.current.unsub === "function") {
          callSubRef.current.unsub();
        }
      } catch {}
      if (callSubRef.current.callId === callId) callSubRef.current = { callId: "", unsub: null };
    };
  }, [callId, me?.uid]);

  /* ---------------- ringtone based on uiStatus ---------------- */
  useEffect(() => {
    if (uiStatus === "incoming") startRingtone();
    else stopRingtone();
    return () => stopRingtone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiStatus]);

  /* ---------------- caller ringing timeout -> missed ---------------- */
  useEffect(() => {
    if (!createdByMeRef.current) return;
    if (!callDoc?.id) return;
    if (safeStr(callDoc.status) !== "ringing") return;

    if (ringTimerRef.current) clearTimeout(ringTimerRef.current);

    ringTimerRef.current = setTimeout(async () => {
      try {
        const ref = doc(db, "calls", callDoc.id);
        await runTransaction(db, async (tx) => {
          const s = await tx.get(ref);
          if (!s.exists()) return;
          const cur = s.data() || {};
          if (safeStr(cur.status) !== "ringing") return;
          tx.update(ref, { status: "missed", endedAt: serverTimestamp(), updatedAt: serverTimestamp() });
        });

        await logCallToChatOnce({ call: { ...callDoc, status: "missed" } });
      } catch (e) {
        console.error("ring timeout miss error:", e);
      }
    }, RING_TIMEOUT_MS);

    return () => {
      if (ringTimerRef.current) clearTimeout(ringTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callDoc?.id, callDoc?.status]);

  /* ---------------- timer ---------------- */
  useEffect(() => {
    const st = safeStr(callDoc?.status);

    if (st !== "answered") {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
      setTimerSec(0);
      return;
    }

    const ansMs = msFromTs(callDoc?.answeredAt) || Date.now();

    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      const s = Math.max(0, Math.floor((Date.now() - ansMs) / 1000));
      setTimerSec(s);
    }, 500);

    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
    };
  }, [callDoc?.status, callDoc?.answeredAt]);

  /* ---------------- start/stop Agora when status changes ---------------- */
  useEffect(() => {
    const st = safeStr(callDoc?.status);
    if (st === "answered") agoraJoinIfNeeded();
    else if (["ended", "missed", "declined"].includes(st)) agoraLeave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callDoc?.status]);

  /* ---------------- keep video fresh ---------------- */
  useEffect(() => {
    if (!showVideoLayout) return;
    if (!agoraJoined) return;
    replayVideo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showVideoLayout, agoraJoined, swapViews, remoteVideoUid]);

  /* ---------------- chat logger ---------------- */
  const logCallToChatOnce = async ({ call }) => {
    if (!me?.uid || !matchId) return;

    const callIdSafe = safeStr(call?.id || callDoc?.id || callId);
    if (!callIdSafe) return;

    const statusSafe = safeStr(call?.status || callDoc?.status);
    const modeSafe = safeStr(call?.mode || callDoc?.mode) || safeStr(mode) || "voice";
    if (!["missed", "ended", "declined"].includes(statusSafe)) return;

    const callsRef = doc(db, "calls", callIdSafe);
    const matchRef = doc(db, "matches", matchId);

    const fromUid = safeStr(call?.fromUid || callDoc?.fromUid);
    const toUid = safeStr(call?.toUid || callDoc?.toUid);

    const answeredMs = msFromTs(call?.answeredAt || callDoc?.answeredAt);
    const endedMs = msFromTs(call?.endedAt || callDoc?.endedAt) || Date.now();
    const durationSec =
      statusSafe === "ended" && answeredMs ? Math.max(0, Math.floor((endedMs - answeredMs) / 1000)) : 0;

    const icon2 = modeSafe === "video" ? "🎥" : "📞";
    const preview =
      statusSafe === "missed"
        ? `${icon2} Missed call`
        : statusSafe === "declined"
        ? `${icon2} Call declined`
        : `${icon2} Call ended`;

    await runTransaction(db, async (tx) => {
      const callSnap = await tx.get(callsRef);
      if (!callSnap.exists()) return;

      const cd = callSnap.data() || {};
      if (cd.chatLogged === true || cd.loggedToChat === true || cd.loggedToChat === true) return;

      const msgRef = doc(collection(db, "matches", matchId, "messages"));
      tx.set(msgRef, {
        type: "call",
        callId: callIdSafe,
        callMode: modeSafe,
        callStatus: statusSafe,
        fromUid,
        toUid,
        durationSec,
        senderUid: fromUid,
        createdAt: serverTimestamp(),
      });

      const matchSnap = await tx.get(matchRef);
      if (matchSnap.exists()) {
        const m = matchSnap.data() || {};
        const otherField = otherUnreadField(m, fromUid);
        tx.set(
          matchRef,
          {
            lastMessageText: preview,
            lastMessageAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            [otherField]: increment(1),
          },
          { merge: true }
        );
      }

      tx.update(callsRef, { chatLogged: true, loggedToChat: true, updatedAt: serverTimestamp() });
    });
  };

  /* ---------------- actions ---------------- */
  const backToChat = () => nav(`/chat/${matchId}`);
  const onSwap = () => setSwapViews((v) => !v);
  const forceRefresh = () => replayVideo(remoteVideoUidRef.current);

  const answer = async () => {
    stopRingtone();
    if (!callId || !me?.uid) return;

    setAgoraErr("");
    dbgPush("answer:click", { callId, meUid: me.uid });

    try {
      const ref = doc(db, "calls", callId);

      await runTransaction(db, async (tx) => {
        const s = await tx.get(ref);
        if (!s.exists()) throw new Error("Call doc missing");

        const cur = s.data() || {};
        dbgPush("answer:read", { status: cur.status, toUid: cur.toUid });

        if (safeStr(cur.status) !== "ringing") return;
        if (safeStr(cur.toUid) !== safeStr(me.uid))
          throw new Error(`toUid mismatch: cur.toUid=${cur.toUid} me.uid=${me.uid}`);

        tx.update(ref, { status: "answered", answeredAt: serverTimestamp(), updatedAt: serverTimestamp() });
      });

      dbgPush("answer:updated");
    } catch (e) {
      const msg = safeStr(e?.message) || "Answer failed";
      setAgoraErr(msg);
      dbgPush("answer:error", { msg });
    }
  };

  const decline = async () => {
    stopRingtone();
    if (!callId || !me?.uid) return;

    try {
      const ref = doc(db, "calls", callId);
      await runTransaction(db, async (tx) => {
        const s = await tx.get(ref);
        if (!s.exists()) return;
        const cur = s.data() || {};
        if (safeStr(cur.status) !== "ringing") return;
        if (safeStr(cur.toUid) !== safeStr(me.uid)) return;

        tx.update(ref, { status: "declined", endedAt: serverTimestamp(), updatedAt: serverTimestamp() });
      });

      await logCallToChatOnce({ call: { ...callDoc, status: "declined" } });
    } catch (e) {
      dbgPush("decline:error", { msg: e?.message });
    }
  };

  const end = async () => {
    stopRingtone();

    if (!callId) {
      nav(`/chat/${matchId}`, { replace: true });
      return;
    }

    try {
      const ref = doc(db, "calls", callId);
      await runTransaction(db, async (tx) => {
        const s = await tx.get(ref);
        if (!s.exists()) return;
        const cur = s.data() || {};
        const curSt = safeStr(cur.status);
        if (!["ringing", "answered"].includes(curSt)) return;

        tx.update(ref, { status: "ended", endedAt: serverTimestamp(), updatedAt: serverTimestamp() });
      });

      await logCallToChatOnce({ call: { ...callDoc, status: "ended" } });
    } catch (e) {
      dbgPush("end:error", { msg: e?.message });
    }
  };

  useEffect(() => {
    if (!callDoc?.id) return;
    const st = safeStr(callDoc.status);
    if (["ended", "missed", "declined"].includes(st)) {
      logCallToChatOnce({ call: callDoc }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callDoc?.status, callDoc?.id]);

  const statusText = useMemo(() => {
    if (uiStatus === "loading") return "Loading…";
    if (uiStatus === "incoming") return "Incoming call…";
    if (uiStatus === "ringing") return "Ringing…";
    if (uiStatus === "in_call") return "In call";
    if (uiStatus === "ended") return "Call ended";
    if (uiStatus === "missed") return "Missed call";
    if (uiStatus === "declined") return "Call declined";
    return "Something went wrong";
  }, [uiStatus]);

  const subText = useMemo(() => {
    if (uiStatus === "in_call") return `Duration: ${fmtMMSS(timerSec)}`;
    if (uiStatus === "ringing") return "Waiting for answer…";
    if (uiStatus === "incoming") return "Answer or decline";
    return "";
  }, [uiStatus, timerSec]);

  const remoteCount = remoteUsersState.length;
  const remoteHasVideo = remoteUsersState.some((r) => r.hasVideo);
  const hasCamTrack = !!tracksRef.current?.cam;

  return (
    <div style={S.page}>
      <div style={S.shell}>
        {/* header */}
        <div style={S.header}>
          <button type="button" onClick={backToChat} style={S.backBtn} title="Back to chat">
            ←
          </button>

          <div style={S.headInfo}>
            <div style={{ position: "relative" }}>
              {other.avatar ? (
                <img src={other.avatar} alt={headTitle} style={S.avatar} />
              ) : (
                <div style={S.avatarFallback}>{initials(headTitle)}</div>
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={S.hTitle} title={headTitle}>
                {headTitle}
              </div>
              <div style={S.hSub}>
                {icon} {safeStr(mode).toUpperCase()} • {statusText}
              </div>
            </div>
          </div>
        </div>

        {agoraErr ? <div style={S.err}>{agoraErr}</div> : null}

        {/* main */}
        <div style={S.card}>
          {showVideoLayout ? (
            <div style={S.videoWrap}>
              <div style={S.bigBox} onClick={onSwap} title="Tap to swap">
                <div ref={bigVideoRef} style={S.videoFill} />
                {!swapViews && !remoteHasVideo ? (
                  <div style={S.overlayHint}>Waiting for their video… (remoteCount={remoteCount})</div>
                ) : null}
                <div style={S.bigLabel}>{swapViews ? "You" : "Them"}</div>
              </div>

              <div style={S.pipWrap}>
                {swapViews ? (
                  <div style={S.pipBox} onClick={onSwap} title="Tap to swap">
                    <div ref={pipVideoRef} style={S.videoFill} />
                    <div style={S.pipLabel}>Them</div>
                  </div>
                ) : hasCamTrack ? (
                  <div style={S.pipBox} onClick={onSwap} title="Tap to swap">
                    <div ref={pipVideoRef} style={S.videoFill} />
                    <div style={S.pipLabel}>You</div>
                  </div>
                ) : (
                  <div style={S.camOffPill} onClick={onSwap} title="Tap to swap">
                    📷 Camera off
                  </div>
                )}
              </div>

              <button type="button" style={S.refreshBtn} onClick={forceRefresh}>
                Force refresh video
              </button>
            </div>
          ) : (
            <>
              <div style={S.bigIcon}>{icon}</div>
              <div style={S.mainText}>{statusText}</div>
              {subText ? <div style={S.subText}>{subText}</div> : null}
            </>
          )}

          {uiStatus === "incoming" ? (
            <div style={S.actions}>
              <button type="button" style={S.btnDecline} onClick={decline}>
                Decline
              </button>
              <button type="button" style={S.btnAnswer} onClick={answer}>
                Answer
              </button>
            </div>
          ) : uiStatus === "ringing" ? (
            <div style={S.actions}>
              <button type="button" style={S.btnEnd} onClick={end}>
                End
              </button>
            </div>
          ) : uiStatus === "in_call" ? (
            <div style={S.actions}>
              <button type="button" style={S.btnEnd} onClick={end}>
                End call
              </button>
            </div>
          ) : uiStatus === "ended" ||
            uiStatus === "missed" ||
            uiStatus === "declined" ||
            uiStatus === "error" ? (
            <div style={S.actions}>
              <button type="button" style={S.btnBack} onClick={backToChat}>
                Back to chat
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ---------------- styles ---------------- */
const S = {
  page: { width: "100%", height: "100vh", overflow: "hidden", background: "rgba(236, 242, 238, 0.85)" },
  shell: {
    width: "100%",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    padding: 12,
    boxSizing: "border-box",
  },

  header: {
    display: "grid",
    gridTemplateColumns: "44px 1fr",
    gap: 10,
    alignItems: "center",
    padding: "6px 0",
    marginBottom: 12,
  },

  backBtn: {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "none",
    background: "rgba(255,255,255,0.7)",
    cursor: "pointer",
    fontSize: 20,
    display: "grid",
    placeItems: "center",
    boxShadow: "0 12px 22px rgba(17,24,39,0.06)",
    fontWeight: 950,
  },

  headInfo: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    borderRadius: 18,
    padding: "10px 12px",
    background: "rgba(255,255,255,0.75)",
    border: "1px solid rgba(17,24,39,0.06)",
    boxShadow: "0 18px 40px rgba(17,24,39,0.08)",
    minWidth: 0,
  },

  avatar: {
    width: 44,
    height: 44,
    borderRadius: 999,
    objectFit: "cover",
    boxShadow: "0 14px 26px rgba(17,24,39,0.12)",
  },
  avatarFallback: {
    width: 44,
    height: 44,
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    background: "linear-gradient(135deg, rgba(59,130,246,0.25), rgba(99,102,241,0.20))",
    color: "#111827",
    fontWeight: 900,
    border: "1px solid rgba(17,24,39,0.10)",
  },

  hTitle: { fontWeight: 950, fontSize: 18, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  hSub: {
    fontWeight: 800,
    opacity: 0.65,
    fontSize: 12,
    marginTop: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  err: {
    padding: "8px 12px",
    marginBottom: 10,
    borderRadius: 14,
    background: "rgba(239,68,68,0.10)",
    border: "1px solid rgba(239,68,68,0.20)",
    color: "#7f1d1d",
    fontWeight: 900,
  },

  card: {
    flex: 1,
    minHeight: 0,
    borderRadius: 22,
    border: "1px solid rgba(17,24,39,0.06)",
    background: "rgba(255,255,255,0.72)",
    boxShadow: "0 18px 40px rgba(17,24,39,0.08)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
    textAlign: "center",
    backdropFilter: "blur(10px)",
    position: "relative",
  },

  bigIcon: { fontSize: 64, marginBottom: 10 },
  mainText: { fontWeight: 1000, fontSize: 22, letterSpacing: -0.2 },
  subText: { marginTop: 6, fontWeight: 900, opacity: 0.7 },

  actions: { display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap", justifyContent: "center" },

  btnAnswer: {
    height: 46,
    padding: "0 18px",
    borderRadius: 14,
    border: "none",
    cursor: "pointer",
    fontWeight: 1000,
    color: "#fff",
    background: "linear-gradient(90deg, rgba(34,197,94,0.98), rgba(16,185,129,0.98))",
    boxShadow: "0 16px 24px rgba(34,197,94,0.18)",
  },
  btnDecline: {
    height: 46,
    padding: "0 18px",
    borderRadius: 14,
    border: "1px solid rgba(239,68,68,0.25)",
    cursor: "pointer",
    fontWeight: 1000,
    color: "#991b1b",
    background: "rgba(239,68,68,0.10)",
    boxShadow: "0 16px 24px rgba(239,68,68,0.10)",
  },
  btnEnd: {
    height: 46,
    padding: "0 18px",
    borderRadius: 14,
    border: "1px solid rgba(239,68,68,0.25)",
    cursor: "pointer",
    fontWeight: 1000,
    color: "#991b1b",
    background: "rgba(239,68,68,0.10)",
    boxShadow: "0 16px 24px rgba(239,68,68,0.10)",
  },
  btnBack: {
    height: 46,
    padding: "0 18px",
    borderRadius: 14,
    border: "1px solid rgba(17,24,39,0.10)",
    cursor: "pointer",
    fontWeight: 1000,
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 16px 24px rgba(17,24,39,0.08)",
  },

  /* video */
  videoWrap: { width: "100%", maxWidth: 980, height: "70vh", position: "relative" },

  bigBox: {
    width: "100%",
    height: "100%",
    borderRadius: 18,
    overflow: "hidden",
    border: "1px solid rgba(17,24,39,0.12)",
    background: "#000",
    position: "relative",
    cursor: "pointer",
  },
  bigLabel: {
    position: "absolute",
    left: 10,
    bottom: 10,
    fontSize: 12,
    fontWeight: 900,
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(0,0,0,0.35)",
    color: "#fff",
  },

  overlayHint: {
    position: "absolute",
    inset: 0,
    display: "grid",
    placeItems: "center",
    color: "rgba(255,255,255,0.92)",
    fontWeight: 950,
    background: "linear-gradient(180deg, rgba(0,0,0,0.18), rgba(0,0,0,0.45))",
    padding: 16,
  },

  pipWrap: { position: "absolute", right: 12, bottom: 12 },

  pipBox: {
    width: 120,
    height: 160,
    borderRadius: 18,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.18)",
    background: "#000",
    position: "relative",
    cursor: "pointer",
  },
  pipLabel: {
    position: "absolute",
    left: 10,
    bottom: 10,
    fontSize: 11,
    fontWeight: 900,
    padding: "5px 9px",
    borderRadius: 999,
    background: "rgba(0,0,0,0.35)",
    color: "#fff",
  },

  camOffPill: {
    width: 120,
    height: 46,
    display: "grid",
    placeItems: "center",
    borderRadius: 999,
    background: "rgba(255,255,255,0.88)",
    border: "1px solid rgba(17,24,39,0.10)",
    fontWeight: 950,
    boxShadow: "0 18px 40px rgba(17,24,39,0.12)",
    cursor: "pointer",
    padding: "0 10px",
  },

  videoFill: { width: "100%", height: "100%" },

  refreshBtn: {
    position: "absolute",
    left: 12,
    bottom: 12,
    height: 40,
    padding: "0 12px",
    borderRadius: 12,
    border: "1px solid rgba(17,24,39,0.10)",
    background: "rgba(255,255,255,0.88)",
    cursor: "pointer",
    fontWeight: 900,
    boxShadow: "0 18px 40px rgba(17,24,39,0.12)",
  },
};